import React from 'react';
import { useStyles } from './styles';
import { Grid} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ListPassword from './ListPassword';
import { SET_PASSWORD } from '../../../../actions/update';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import {Box, ButtonV2, Icon, ICONS_NAME, InputV2, Typography} from "@/elements";
import {trimHandler} from "@/utils/trimHandler.js";
import {useToggle} from "@hooks";

const Password = ({ user, data }) => {
    const classes = useStyles();

    const [showOldPassword, setShowOldPassword] = useToggle(false);
    const [showNewPassword, setShowNewPassword] = useToggle(false);
    const [showDoubleNewPassword, setShowDoubleNewPassword] = useToggle(false);

    const [setPassword] = useMutation(SET_PASSWORD);

    const onSubmit = inputData => {
        setPassword({
            variables: {
                oldPassword: inputData.oldPassword,
                newPassword: inputData.newPassword,
                email: data.partnerAccountEmployee.email,
                type: user && user.type,
            },
        })
            .then(() => {
                toast.success('Success');
            })
            .catch(err => toast.error(err.message));
    };

    const checkUppercase = /([A-Z])|([А-Я])/;
    const checkLowercase = /([a-z])|([а-я])/;
    const checkNumber = /([0-9])/;
    const checkSpecialCharacter = /[^A-Za-zА-Яа-яёЁ0-9\s]/;

    const validationSchema = Yup.object().shape({
        oldPassword: Yup.string().required('Current Password is required'),
        newPassword: Yup.string()
            .min(8, 'New Password must be at least 8 characters')
            .matches(checkUppercase, 'Password must have one uppercase character')
            .matches(checkLowercase, 'Password must have one lowercase character')
            .matches(checkNumber, 'Password must have one number')
            .matches(checkSpecialCharacter, 'Password must have one special character')
            .required('Current New Password is required'),
        doubleNewPassword: Yup.string()
            .oneOf([Yup.ref('newPassword'), null], 'Confirm New Password must match')
            .required('Confirm New Password is required'),
    });

    const {
        handleSubmit,
        watch,
        formState: { errors },
        control,
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    const inputVariable = !watch('newPassword') ? '' : watch('newPassword');

    return (
        <Box direction="column" gap="20px" $minWidth="340px">
            <Typography variant="h6">
                Change password
            </Typography>
            <Grid container>
                <Grid item className={classes.form}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box direction="column" gap="20px" width="550px">
                        <Controller
                            name="oldPassword"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, onBlur, value } }) => (
                                <InputV2
                                    error={!!errors.oldPassword}
                                    type={showOldPassword ? 'text' : 'password'}
                                    value={value}
                                    label="Current Password"
                                    onChange={onChange}
                                    onBlur={(e) => {
                                        trimHandler(e.target.value, onBlur, onChange)
                                    }}
                                    width="100%"
                                    helperText={errors?.oldPassword?.message ?? ' '}
                                    rightAdornment={
                                        <Icon
                                            icon={!showOldPassword ? ICONS_NAME.eyeOn : ICONS_NAME.eyeOff}
                                            cursor="pointer"
                                            onClick={setShowOldPassword}
                                        />
                                    }
                                />
                            )}
                        />
                        <Controller
                            name="newPassword"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, onBlur, value } }) => (
                                <InputV2
                                    error={!!errors.newPassword}
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={
                                        value.charCodeAt(value.length - 1) >= 33 && value.charCodeAt(value.length - 1) <= 126
                                            ? value
                                            : value.replace(value[value.length - 1], '')
                                    }
                                    label="New Password"
                                    onChange={onChange}
                                    onBlur={(e) => {
                                        trimHandler(e.target.value, onBlur, onChange)
                                    }}
                                    variant="filled"
                                    helperText={errors?.newPassword?.message ?? ' '}
                                    className={classes.input}
                                    rightAdornment={
                                        <Icon
                                            icon={showNewPassword ? ICONS_NAME.eyeOff : ICONS_NAME.eyeOn}
                                            cursor="pointer"
                                            onClick={setShowNewPassword}
                                        />
                                    }
                                />
                            )}
                        />
                        <div className={classes.listPass1}>
                            <ListPassword
                                inputVariable={inputVariable}
                                checkUppercase={checkUppercase}
                                checkLowercase={checkLowercase}
                                checkNumber={checkNumber}
                                checkSpecialCharacter={checkSpecialCharacter}
                            />
                        </div>

                        <Controller
                            name="doubleNewPassword"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, onBlur, value } }) => (
                                <InputV2
                                    type={showDoubleNewPassword ? 'text' : 'password'}
                                    value={value}
                                    label="Confirm New Password"
                                    onChange={onChange}
                                    variant="filled"
                                    className={classes.input}
                                    error={!!errors.doubleNewPassword}
                                    onBlur={(e) => {
                                        trimHandler(e.target.value, onBlur, onChange)
                                    }}
                                    helperText={errors?.doubleNewPassword?.message ?? ' '}
                                    rightAdornment={
                                        <Icon
                                            icon={showDoubleNewPassword ? ICONS_NAME.eyeOff : ICONS_NAME.eyeOn}
                                            cursor="pointer"
                                            onClick={setShowDoubleNewPassword}
                                        />
                                    }
                                />
                            )}
                        />
                        <div className={classes.buttonWrapper}>
                            <ButtonV2 type="submit">
                                restore password
                            </ButtonV2>
                        </div>
                        </Box>
                    </form>
                </Grid>

                <Grid item xs className={classes.listPass2}>
                    <ListPassword
                        inputVariable={inputVariable}
                        checkUppercase={checkUppercase}
                        checkLowercase={checkLowercase}
                        checkNumber={checkNumber}
                        checkSpecialCharacter={checkSpecialCharacter}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default Password;
